import React, { Dispatch, useMemo } from 'react';
import {
  Button,
  Table,
  TableRow,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableEditableCell,
  Box,
  TableCell,
  Text,
  AlertCircleIcon,
  TrashIcon,
} from '@razorpay/blade/components';
import { ArrearMonth, ArrearsAction } from './types';
import { DeductionsComponentType, EarningsComponentType } from 'components/SalaryComponents/types';
import { ComponentSelector } from './ComponentSelector';
import { useArrearsComponetsList } from './hooks/useArrearsComponetsList';
import { getComponentName, getNewArrearComponent, isComplianceComponent } from './utils';
import useFeature from 'hooks/useFeature';

export const ArrearComponents = ({
  arrearId,
  monthWiseBreakdown,
  dispatch,
  isComputationNeeded,
  clearPreviewCache,
  isSubmitting,
}: {
  arrearId: string;
  monthWiseBreakdown: ArrearMonth;
  dispatch: Dispatch<ArrearsAction>;
  isComputationNeeded: boolean;
  clearPreviewCache: () => void;
  isSubmitting: boolean;
}) => {
  const { isEsiArrearEnabled } = useFeature('esiArrear');
  const isPfArrearEnabled = useFeature('pf-arrears')['isPf-arrearsEnabled'];
  const monthWiseComponents = monthWiseBreakdown.components;

  const { searchSalaryComponents, isFetchingSalaryComponents } = useArrearsComponetsList();

  const alreadyAddedComponents = monthWiseComponents.map((component) => component.componentId);

  const onDeleteComponent = (componentId: string) => {
    dispatch({
      type: 'COMPONENT_DELETED',
      payload: { arrearId, componentId, isPfArrearEnabled, isEsiArrearEnabled },
    });

    clearPreviewCache();
  };

  const onAddNewComponent = () => {
    const newComponent = getNewArrearComponent();
    dispatch({ type: 'COMPONENT_ADDED', payload: { arrearId, component: newComponent } });
    clearPreviewCache();
  };

  const onComponentAmountChange = ({
    componentToUpdate,
    amount,
  }: {
    componentToUpdate: ArrearMonth['components'][0];
    amount: string;
  }) => {
    dispatch({
      type: 'COMPONENT_AMOUNT_CHANGED',
      payload: {
        arrearId,
        componentId: componentToUpdate.id,
        amount,
        isEsiArrearEnabled,
        isPfArrearEnabled,
      },
    });
    clearPreviewCache();
  };

  const onUpdateComponent = (
    componentId: string,
    updates: Partial<ArrearMonth['components'][0]>,
  ) => {
    dispatch({
      type: 'COMPONENT_UPDATED',
      payload: { arrearId, componentId, updates },
    });
    clearPreviewCache();
  };

  const totalNumberOfComponents = monthWiseComponents.length ?? 0;

  const sortedComponents = useMemo(() => {
    return [...monthWiseComponents].sort((component1, component2) => {
      // Basic comes first
      if (component1.componentName === 'Basic') return -1;
      if (component2.componentName === 'Basic') return 1;

      // Compliance components come last
      const isComponent1Compliance = isComplianceComponent(component1.sdiType);
      const isComponent2Compliance = isComplianceComponent(component2.sdiType);

      if (isComponent1Compliance && !isComponent2Compliance) return 1;
      if (!isComponent1Compliance && isComponent2Compliance) return -1;

      // Special Allowance comes just before compliance
      if (component1.componentName === 'Special Allowance' && !isComponent2Compliance) return 1;
      if (component2.componentName === 'Special Allowance' && !isComponent1Compliance) return -1;

      return 0;
    });
  }, [monthWiseComponents]);

  return (
    <>
      <Table
        rowDensity="compact"
        showBorderedCells
        data={{ nodes: sortedComponents }}
        gridTemplateColumns="1fr 1fr 52px">
        {(items) => (
          <>
            <TableHeader>
              <TableHeaderCell>Name</TableHeaderCell>
              <TableHeaderCell>Amount</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableHeader>
            <TableBody>
              {items.map((component) => {
                const isComplianceComponentType = isComplianceComponent(component.sdiType);
                const needsRecomputation = isComputationNeeded && isComplianceComponentType;
                const isComponentEditable = !isComplianceComponentType && !component.isSaved;
                const isComponentDeletable =
                  !isComplianceComponentType && totalNumberOfComponents > 1;

                return (
                  <TableRow item={component} key={component.id}>
                    {isComponentEditable ? (
                      <ComponentSelector
                        options={searchSalaryComponents(
                          alreadyAddedComponents.filter((id) => id !== component.componentId),
                        )}
                        selectedComponent={component}
                        isDisabled={isFetchingSalaryComponents || isSubmitting}
                        data-testid={`month-${arrearId}-component-${component.id}`}
                        name={`month-${arrearId}-component-${component.id}`}
                        onChange={(item: EarningsComponentType | DeductionsComponentType) => {
                          onUpdateComponent(component.id, {
                            componentId: item.id,
                            componentSource: item.componentSource,
                            componentName: item.name,
                            sdiType:
                              item.category === 'EARNINGS_CATEGORY'
                                ? 'earnings_v1'
                                : item.category === 'DEDUCTIONS_CATEGORY'
                                ? 'deduction_v1'
                                : undefined,
                          });
                        }}
                        onClearSelection={() => {
                          onUpdateComponent(component.id, {
                            componentId: '',
                            componentName: '',
                            componentSource: undefined,
                            sdiType: undefined,
                          });
                        }}
                      />
                    ) : (
                      <TableCell>
                        <Text marginLeft="spacing.2">
                          {getComponentName({
                            name: component.componentName,
                            sdiType: component.sdiType,
                          })}
                        </Text>
                      </TableCell>
                    )}
                    {needsRecomputation ? (
                      <TableCell>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="spacing.2"
                          marginLeft="spacing.2">
                          <AlertCircleIcon color="feedback.icon.notice.intense" />
                          <Text color="surface.text.gray.muted">Needs recomputation</Text>
                        </Box>
                      </TableCell>
                    ) : (
                      <TableEditableCell
                        accessibilityLabel={`${component.componentName} amount`}
                        inputType="text"
                        value={String(component.amount)}
                        isDisabled={isComplianceComponentType || isSubmitting}
                        onChange={({ value }) => {
                          // Check if value is an integer (negative, positive, or zero)
                          const isInteger = /^-?\d*$/.test(value?.trim() || '');
                          if (!isInteger && value?.trim() !== '') {
                            return;
                          }
                          onComponentAmountChange({
                            componentToUpdate: component,
                            amount: value ?? '',
                          });
                        }}
                      />
                    )}
                    <TableCell>
                      {isComponentDeletable ? (
                        <Button
                          isDisabled={isSubmitting}
                          onClick={() => onDeleteComponent(component.id)}
                          icon={TrashIcon}
                          variant="tertiary"
                          size="xsmall"
                          accessibilityLabel="delete component"
                        />
                      ) : null}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </>
        )}
      </Table>
      <Box
        borderColor="surface.border.gray.muted"
        backgroundColor="surface.background.gray.intense"
        borderWidth="thin"
        borderTopWidth="none"
        padding="spacing.4">
        <Button
          isDisabled={isSubmitting}
          size="xsmall"
          variant="tertiary"
          onClick={() => onAddNewComponent()}>
          Add Component
        </Button>
      </Box>
    </>
  );
};
